import {
    Box,
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {useTranslation} from "react-i18next";
import {ProfileActiveStay} from "crqlar-react-common/dist/models/profile/profileActiveStay.model";
import {ReservationStatusElement} from "../../common/uiElements/ReservationStatusElement";

interface StaysTableProps {
    current?: ProfileActiveStay;
    active?: ProfileActiveStay[];
    upcoming?: ProfileActiveStay[];
    past?: ProfileActiveStay[];
}

const nightsBetween = (start?: string, end?: string): number | undefined => {
    if (!start || !end) return undefined;
    const from = new Date(start).getTime();
    const to = new Date(end).getTime();
    if (isNaN(from) || isNaN(to)) return undefined;
    const nights = Math.round((to - from) / 86_400_000);
    return nights >= 0 ? nights : undefined;
};

const StaySectionTable = ({title, stays, emptyText}: {
    title: string;
    stays: ProfileActiveStay[];
    emptyText: string;
}) => {
    const {t} = useTranslation();

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                {title} ({stays.length})
            </Typography>

            <TableContainer>
                <Table size="small" sx={{tableLayout: 'fixed'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 80}}/>
                        <col style={{width: 110}}/>
                        <col style={{width: 160}}/>
                        <col style={{width: 110}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('Period')}</TableCell>
                            <TableCell align="right">{t('Nights')}</TableCell>
                            <TableCell>{t('Room')}</TableCell>
                            <TableCell>{t('Status')}</TableCell>
                            <TableCell align="right">{t('Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stays.length === 0 &&
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
                                </TableCell>
                            </TableRow>
                        }

                        {stays.map((stay, index) =>
                            <TableRow key={stay.reservationId ?? index}>
                                <TableCell>{[stay.startDay, stay.endDay].filter(Boolean).join(' – ') || '-'}</TableCell>
                                <TableCell align="right">{nightsBetween(stay.startDay, stay.endDay) ?? '-'}</TableCell>
                                <TableCell>{stay.roomId || '-'}</TableCell>
                                <TableCell><ReservationStatusElement status={stay.status}/></TableCell>
                                <TableCell align="right">
                                    {stay.reservationId &&
                                        <Button
                                            size="small"
                                            endIcon={<OpenInNewIcon/>}
                                            sx={{minWidth: 'fit-content'}}
                                            onClick={() => window.open(`/system/reservations/${stay.reservationId}`, '_blank')}>
                                            {t('View')}
                                        </Button>
                                    }
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const StaysTable = ({current, active, upcoming, past}: StaysTableProps) => {
    const {t} = useTranslation();

    // currentStay is itself an active stay — show it in the active table without duplicating it.
    const activeStays: ProfileActiveStay[] = current
        ? [current, ...(active ?? []).filter(stay => stay.reservationId !== current.reservationId)]
        : (active ?? []);

    return (
        <Stack spacing={3}>
            <StaySectionTable
                title={t('Active Stays')}
                stays={activeStays}
                emptyText={t('No active stays')}/>
            <StaySectionTable
                title={t('Upcoming Stays')}
                stays={upcoming ?? []}
                emptyText={t('No upcoming stays')}/>
            <StaySectionTable
                title={t('Past Stays')}
                stays={past ?? []}
                emptyText={t('No past stays')}/>
        </Stack>
    );
};

export default StaysTable;
